import { useCallback, useMemo } from "react";
import { Close } from "@common/schemas/close.schema";
import { useStartAgentRunMutation } from "@api/agentRun.api";
import { useWorkspaceCloses } from "@api/close.api";
import { useEntitiesById } from "@hooks/useEntities";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getActionsTasksAccountsNavPaths } from "@components/navigation/getActionsTasksAccountsNavPaths";
import { CommandsDirectory } from "./lib";
import { useCommands } from "./useCommands";

export function useStartCloseAgent() {
    const closesQuery = useWorkspaceCloses();
    const entitiesById = useEntitiesById();
    const toaster = useToaster();
    const navigate = useStateAwareNavigate();
    const { open: openCommands } = useCommands();

    const entityName = useCallback(
        (close: Pick<Close, "entityId">): string =>
            entitiesById.get(close.entityId)?.name ?? "entity",
        [entitiesById],
    );

    // The command palette unmounts the caller before the request settles.
    // Per-call mutate() callbacks are dropped on unmount; these hook-level
    // callbacks still run, so the outcome is handled here.
    const startRun = useStartAgentRunMutation({
        onSuccess: ({ runTaskId }, params) => {
            toaster.success(`Started Kick agent for ${entityName(params)}`);
            const { tasksTabPath } = getActionsTasksAccountsNavPaths();
            navigate(`${tasksTabPath}?taskId=${runTaskId}`);
        },
        onError: () => {
            toaster.error("Failed to start the agent");
        },
    });

    const openCloses = useMemo(
        () =>
            (closesQuery.data ?? []).filter(
                (close) => close.cancelledAt === null,
            ),
        [closesQuery.data],
    );

    const start = useCallback(
        (close: Close): void => {
            startRun.mutate({ closeId: close.id, entityId: close.entityId });
        },
        [startRun],
    );

    // One close starts at once; several open the "Pick close" directory.
    // The command palette and the Agent panel share this path.
    const startFromPicker = useCallback((): void => {
        if (openCloses.length === 0) {
            toaster.error("Create a close first");
            return;
        }
        if (openCloses.length === 1) {
            start(openCloses[0]);
            return;
        }
        openCommands(CommandsDirectory.AGENT_RUN_CLOSE);
    }, [openCloses, start, openCommands, toaster]);

    return {
        isReady: closesQuery.isSuccess,
        openCloses,
        entityName,
        start,
        startFromPicker,
    };
}
